/**
 * Tool registry and dispatcher.
 *
 * Owns which tool is active and forwards pointer and key events to it. The
 * shell asks this for the tool list to render and nothing more.
 */

import type { DocumentKind } from '@sircad/core/model.ts';
import { preferredLayer } from '@sircad/core/geom/board.ts';
import type { Point } from '../render/viewport.ts';
import type { Tool, ToolContext } from './tool.ts';
import { SelectTool, type CarryEnd } from './select.ts';
import { TrackTool } from './track.ts';
import { ArcTool, JunctionTool, PadTool, TextTool, ViaTool } from './place.ts';
import { GrabVertexTool, VertexTool } from './vertex.ts';
import { ArcPointsTool, FillTool } from './shapes.ts';
import { ArcHandleTool } from './archandle.ts';

export type { Tool, ToolContext } from './tool.ts';

export class ToolBox {
  readonly tools: Tool[] = [
    new SelectTool(),
    // Two commands, one implementation: the conductive run, and the plain
    // drawn line that must not touch the active layer or the net.
    new TrackTool(true),
    new TrackTool(false),
    new PadTool(),
    new ViaTool(),
    new JunctionTool(),
    new ArcTool(),
    new TextTool(),
    new VertexTool(),
    new GrabVertexTool(),
    new FillTool(),
    new ArcPointsTool(),
    // The three grips on a curve. One implementation, because they differ only
    // in which point the cursor takes hold of.
    new ArcHandleTool('origin', 'g'),
    new ArcHandleTool('end', 'd'),
    new ArcHandleTool('centre', 'v'),
  ];

  private current: Tool = this.tools[0]!;

  // Not a constructor parameter property: node's type stripping cannot erase
  // one, and the headless checks import this module through node.
  private readonly ctx: ToolContext;

  constructor(ctx: ToolContext) {
    this.ctx = ctx;
  }

  get active(): Tool {
    return this.current;
  }

  /** Tools valid for a document kind, in registry order. */
  available(kind: DocumentKind): Tool[] {
    return this.tools.filter((t) => !t.appliesTo || t.appliesTo.includes(kind));
  }

  select(id: string): void {
    const next = this.tools.find((t) => t.id === id);
    if (!next || next === this.current) return;

    this.current.cancel?.(this.ctx);
    this.current.onDeactivate?.(this.ctx);
    this.current = next;
    this.ctx.preview.length = 0;
    this.applyPreferredLayer(next);
    next.onActivate?.(this.ctx);
    this.ctx.setMessage(next.hint);
    this.ctx.redraw();
  }

  /**
   * Move the active layer to one this tool belongs on.
   *
   * Without this, geometry lands on whatever layer was last selected — traces
   * on silkscreen, board text on copper. A layer that already qualifies is
   * kept, so choosing a specific copper layer is never overridden.
   */
  private applyPreferredLayer(tool: Tool): void {
    const doc = this.ctx.doc();
    const types = tool.layerTypes?.[doc.kind];
    if (!types || types.length === 0) return;

    const next = preferredLayer(doc, types, this.ctx.activeLayer);
    if (next !== this.ctx.activeLayer) this.ctx.setActiveLayer(next);
  }

  /** Re-apply the active tool's layer preference, e.g. after a mode switch. */
  refreshLayer(): void {
    this.applyPreferredLayer(this.current);
  }

  /** Switch by keyboard shortcut. Returns true if a tool matched. */
  selectByKey(key: string, kind: DocumentKind): boolean {
    const lower = key.toLowerCase();
    const tool = this.available(kind).find((t) => t.key === lower);
    if (!tool) return false;
    this.select(tool.id);
    return true;
  }

  /**
   * If the active tool is not valid for this document kind — the via tool on a
   * schematic, say — fall back to select rather than leaving it stranded.
   */
  ensureValid(kind: DocumentKind): void {
    if (this.current.appliesTo && !this.current.appliesTo.includes(kind)) {
      this.select('select');
    }
  }

  pointerDown(p: Point, ev?: MouseEvent): void {
    this.current.onPointerDown?.(p, this.ctx, ev as MouseEvent);
  }

  pointerMove(p: Point, ev?: MouseEvent): void {
    this.current.onPointerMove?.(p, this.ctx, ev as MouseEvent);
  }

  pointerUp(p: Point, ev: MouseEvent): void {
    this.current.onPointerUp?.(p, this.ctx, ev);
  }

  doubleClick(p: Point, ev: MouseEvent): void {
    this.current.onDoubleClick?.(p, this.ctx, ev);
  }

  key(ev: KeyboardEvent): boolean {
    return this.current.onKey?.(ev, this.ctx) ?? false;
  }

  /**
   * Hand the plain cursor whatever is selected, already in hand, from `from`.
   *
   * Block Move's way in. Switching to select first, because that is the tool
   * that knows how to carry: a block on the cursor should behave like anything
   * else on the cursor, down to the arrow keys and Escape.
   */
  carry(from: Point, ending?: CarryEnd): void {
    this.select('select');
    const tool = this.current;
    if (tool instanceof SelectTool) tool.carry(this.ctx, from, ending);
  }

  /**
   * Break the run for a via, if a track is being drawn.
   *
   * The layer keys use it: a via wants a vertex under it, and the ordinary
   * click would end the run whenever the cursor is still on the last corner.
   */
  viaCorner(p: Point): void {
    const tool = this.current;
    if (tool instanceof TrackTool) tool.viaCorner(p, this.ctx);
  }

  /** True while the active tool is carrying something on the cursor. */
  get holding(): boolean {
    return this.current.holding?.() ?? false;
  }

  /**
   * True while a run of segments is being drawn.
   *
   * The tool, not its name. Place Signal Wire and Place Line are two commands
   * over one {@link TrackTool} and their ids differ — `track` and `line` — so
   * asking for the id missed every route drawn with Place Line, which on a
   * board is how a great deal of copper gets laid. Place Via then fell through
   * to its parameter dialog and threw the half-drawn run away.
   */
  get routing(): boolean {
    return this.current instanceof TrackTool && this.busy;
  }

  /** True while the active tool has something Escape would abandon. */
  get busy(): boolean {
    return this.current.busy?.() ?? false;
  }

  /** Whether the plain cursor is what is active. */
  get isNeutral(): boolean {
    return this.current.id === 'select';
  }

  cancel(): void {
    this.current.cancel?.(this.ctx);
  }

  /**
   * End the current operation the way the original ends it.
   *
   * A tool that can complete does; one that is only holding something lets go
   * and puts it back. That is the difference between Place Filled Solid, whose
   * help says Escape completes it, and Block Move, whose help says Escape
   * returns the items to where they were.
   */
  end(): void {
    if (this.current.finish) this.current.finish(this.ctx);
    else this.current.cancel?.(this.ctx);
  }
}
